<template>
  <div class="user-management">
    <!-- Controls Section -->
    <div class="controls-section">
  <div class="row align-items-center">
  

    <div class="col-xl-3 col-lg-4 col-md-6 col-12 mb-3 mb-lg-0">
      <select class="filter-select w-100" v-model="userFilter" @change="loadTests">
        <option :value="null">All Users</option>
        <option v-for="user in users" :key="user.id" :value="user.id">
          {{ user.fullName }}
        </option>
      </select>
    </div>

    <div class="col-xl-4 col-lg-4 col-md-6 col-12 mb-3 mb-lg-0">
      <app-disease-tree-selector
        :branchId.sync="branchFilter"
        class="branch-tree-select"
        :clearable="true"
      />
    </div>

    <div class="col-xl-3 col-lg-4 col-md-6 col-12 mb-3 mb-lg-0">
      <select class="filter-select w-100" v-model="dateFilter" @change="loadTests">
        <option value="">All Time</option>
        <option value="today">Today</option>
        <option value="yesterday">Yesterday</option>
        <option value="thisweek">This Week</option>
        <option value="lastweek">Last Week</option>
        <option value="thismonth">This Month</option>
        <option value="lastmonth">Last Month</option>
        <option value="thisyear">This Year</option>
        <option value="lastyear">Last Year</option>
      </select>
    </div>

    <div class="col-xl-2 col-lg-12 col-md-6 col-12">
      <div class="action-buttons d-flex gap-2">
        <button class="btn btn-secondary flex-fill" @click="exportTests" :disabled="loading">
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          Export
        </button>
        <button class="btn btn-primary flex-fill" @click="refreshData" :disabled="loading">
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          Refresh
        </button>
      </div>
    </div>
  </div>
</div>
    <!-- Loading Indicator -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading tests...</p>
    </div>

    <!-- AG-Grid Container -->
    <div class="grid-container" v-else>
      <ag-grid-vue
        class="ag-theme-alpine ag-grid-custom"
        :columnDefs="columnDefs"
        :rowData="tests"
        :defaultColDef="defaultColDef"
        :pagination="true"
        :paginationPageSize="50"
        :paginationPageSizeSelector="[50, 100, 200]"
        :rowSelection="'multiple'"
        :animateRows="true"
        :suppressCellFocus="true"
        :getRowStyle="getRowStyle"
        @grid-ready="onGridReady"
      />
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import { HttpHandler } from '@/http/HttpHandler';
import AppDiseaseTreeSelector from '@/components/AppDiseaseTreeSelector.vue';
import { toastService } from '@/services/toastService';
import '@/assets/css/ag-grid-custom.css';

export default {
  name: 'TestDetails',
  components: {
    AgGridVue,
    AppDiseaseTreeSelector
  },
  data() {
    return {
      gridApi: null,
      httpHandler: null,
      loading: false,
      searchQuery: '',
      branchFilter: null,
      dateFilter: '',
      searchTimeout: null,
      
      tests: [],
      branchMap: {},
      users: [],
      userFilter: null,
      
      columnDefs: [
        { 
          field: 'questionnaireName', 
          headerName: 'Branch Name', 
          width: 200,
          filter: false,
        },
        { 
          field: 'givenByName', 
          headerName: 'User Name', 
          width: 200,
          filter: false,
          cellRenderer: this.userNameRenderer,
        },
        { 
          field: 'totalQuestionsAnswered', 
          headerName: 'Questions Answered', 
          width: 100,
          type: 'numericColumn',
          cellStyle: { textAlign: 'center' },
          filter: false,
        },
        { 
          field: 'createdOn', 
          headerName: 'Start Date', 
          width: 180,
          cellRenderer: this.dateRenderer,
          filter: false,
        },
        { 
          field: 'duration', 
          headerName: 'Duration / Status', 
          width: 180,
          cellRenderer: this.durationRenderer,
          filter: false,
        },
      ],
      defaultColDef: {
        sortable: true,
        filter: false,
        resizable: false,
        floatingFilter: false
      }
    };
  },

  watch: {
    branchFilter(newVal) {
      console.log('Branch filter changed:', newVal);
      this.loadTests();
    },
    userFilter(newVal) {
      console.log('User filter changed:', newVal);
      this.loadTests();
    },
    '$route.query.userId'(newUserId) {
    if (newUserId) {
      this.userFilter = parseInt(newUserId);
      this.loadTests();
    }
  }
  },
  
  async created() {
  try {
    this.httpHandler = new HttpHandler(this.$store);
    await this.loadBranchMap();
    await this.loadUsers();
    
    // Check for userId in URL query parameters
    const userId = this.$route.query.userId;
    if (userId) {
      this.userFilter = parseInt(userId);
    }
    
    await this.loadTests();
  } catch (error) {
    console.error('Error in created():', error);
    toastService.showError('Failed to initialize test management');
  }
},

  

  methods: {
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.applySearchFilter();
      }, 300);
    },

    
    async loadUsers() {
      try {
        const response = await this.httpHandler.getAdminUsers({});
        console.log('Users API Response:', response);
        
        // Handle different response structures
        let usersData = [];
        if (response.users && Array.isArray(response.users)) {
          usersData = response.users;
        } else if (response.data && Array.isArray(response.data)) {
          usersData = response.data;
        } else if (Array.isArray(response)) {
          usersData = response;
        } else if (response.success && response.data) {
          usersData = response.data;
        }
        
        this.users = usersData.map(user => ({
          id: user.id,
          fullName: `${user.name || ''} ${user.surname || ''}`.trim() || 'Unknown User'
        }));
        
        console.log('Users loaded:', this.users.length, 'users');
      } catch (error) {
        console.error('Error loading users:', error);
        console.error('Error details:', error.response || error.message);
        // Don't show error toast, just log it and continue
        this.users = [];
      }
    },

    async loadBranchMap() {
      try {
        const diseaseTree = await this.httpHandler.getDiseaseTree(false);
        this.extractBranchMap(diseaseTree);
        console.log('Branch map loaded:', Object.keys(this.branchMap).length, 'branches');
      } catch (error) {
        console.error('Error loading branch map:', error);
        toastService.showError('Failed to load branches');
      }
    },

    extractBranchMap(node) {
      if (node.id && node.name) {
        this.branchMap[node.id] = node.name;
      }
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          this.extractBranchMap(child);
        });
      }
    },

    getBranchName(branchId) {
      return this.branchMap[branchId] || 'Unknown Branch';
    },

    async loadTests() {
      this.loading = true;
      
      try {
        const filters = {};
        
        if (this.branchFilter !== null && this.branchFilter !== undefined) {
          filters.branchId = this.branchFilter;
        }
        
        if (this.dateFilter && this.dateFilter !== '') {
          filters.dateRange = this.dateFilter;
        }

        if (this.userFilter !== null && this.userFilter !== undefined) {
          filters.givenBy = this.userFilter;
        }

        console.log('Loading tests with filters:', filters);
        const response = await this.httpHandler.getAdminQuestionnaires(filters);
        
        const testsData = response.data || response || [];
        
        this.tests = testsData.map(test => ({
          ...test,
          status: test.duration ? 'completed' : 'incomplete',
          branchName: this.getBranchName(test.branchId),
          testIdentifier: test.testIdentifier || 'N/A',
          givenByName: test.givenByName || 'Unknown User',
          totalQuestionsAnswered: test.totalQuestionsAnswered || 0,
          createdOn: test.createdOn || null,
          duration: test.duration || null,
          isCompleted: test.isCompleted || false
        }));
        
        // Apply search filter after loading
        this.applySearchFilter();
        
      } catch (error) {
        console.error('Error in loadTests:', error);
        toastService.showError('Failed to load tests');
      } finally {
        this.loading = false;
      }
    },

    applySearchFilter() {
      if (!this.gridApi) return;

      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const searchLower = this.searchQuery.toLowerCase().trim();
        
        this.gridApi.setQuickFilter(searchLower);
      } else {
        this.gridApi.setQuickFilter('');
      }

      console.log(`Applied search filter: "${this.searchQuery}"`);
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      params.api.sizeColumnsToFit();
    },

    userNameRenderer(params) {
      const userName = params.value || 'Unknown User';
      const initials = userName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
      
      return `
        <div class="user-name-cell">
          <div class="user-grid-avatar">${initials}</div>
          <span>${userName}</span>
        </div>
      `;
    },
    
    dateRenderer(params) {
      if (!params.value) return 'N/A';
      const date = new Date(params.value);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    durationRenderer(params) {
  const { duration } = params.data;
  
  // Check if duration is missing, null, undefined, empty string, or "00:00:00"
  if (!duration || duration === '00:00:00') {
    return `<span class="status-badge status-incomplete">Incomplete</span>`;
  }
  
  return `<span class="duration-text">${duration}</span>`;
},
    
    getRowStyle(params) {
      const status = params.data.status;
      
      if (status === 'incomplete') {
        return { background: 'rgba(239, 68, 68, 0.03)' };
      } else if (status === 'completed') {
        return { background: 'rgba(34, 197, 94, 0.03)' };
      }
      
      return null;
    },
    
    exportTests() {
      if (this.gridApi) {
        this.gridApi.exportDataAsCsv({
          fileName: 'proxymed_tests_' + new Date().toISOString().split('T')[0] + '.csv',
          columnKeys: [
            'testIdentifier',
            'branchName',
            'givenByName',
            'totalQuestionsAnswered',
            'createdOn',
            'duration',
            'status'
          ],
          processHeaderCallback: (params) => {
            return params.column.getColDef().headerName || params.column.getColId();
          }
        });
        toastService.showSuccess('Export completed successfully!');
      }
    },

    async refreshData() {
      await this.loadTests();
      toastService.showSuccess('Data refreshed successfully!');
    }
  },
  
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
};
</script>

<style scoped>
.user-management {
  position: relative;
  overflow: visible !important;
}

.controls-section {
  position: relative;
  z-index: 100;
  margin-bottom: 1.5rem;
  overflow: visible !important;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: nowrap;
}

.search-filters {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #667bf2;
  box-shadow: 0 0 0 3px rgba(102, 123, 242, 0.1);
}

.search-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.branch-tree-select {
  width: 100%;
  position: relative;
  z-index: 110;
}

.filter-select {
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
  white-space: nowrap;
}

.filter-select:focus {
  outline: none;
  border-color: #667bf2;
  box-shadow: 0 0 0 3px rgba(102, 123, 242, 0.1);
}

.action-buttons {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
}

.btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(47, 69, 165, 0.3);
}

.btn-secondary {
  background: rgba(102, 123, 242, 0.1);
  color: #2f45a5;
  border: 1px solid rgba(102, 123, 242, 0.2);
}

.btn-secondary:hover:not(:disabled) {
  background: rgba(102, 123, 242, 0.15);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  gap: 1rem;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #667bf2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.grid-container {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  position: relative;
  z-index: 1;
  margin-top: 1rem;
}

.user-name-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-grid-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667bf2, #2f45a5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.85rem;
}

.duration-text {
  color: #1e293b;
  font-weight: 500;
}

.status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-incomplete {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

@media (max-width: 768px) {
  .controls-section {
    flex-wrap: wrap;
  }
  
  .search-filters {
    flex-direction: column;
    width: 100%;
  }
  
  .action-buttons {
    width: 100%;
  }
  
  .btn {
    flex: 1;
  }
}
</style>

<style>
/* Global unscoped styles for vue-treeselect dropdown */
.vue-treeselect__menu-container,
.vue-treeselect__menu {
  z-index: 9999 !important;
  position: absolute !important;
}

.vue-treeselect--open-below .vue-treeselect__menu {
  top: 100% !important;
  bottom: auto !important;
}

/* Padding adjustments for tree selector to match other inputs */
.branch-tree-select .vue-treeselect__control {
  padding: 0.5rem 0.75rem !important;
  border-radius: 8px !important;
  border-color: #e2e8f0 !important;
  min-height: 46px !important;
}

.branch-tree-select .vue-treeselect__placeholder,
.branch-tree-select .vue-treeselect__single-value {
  line-height: 1.5 !important;
  padding: 0.25rem 0 !important;
}
</style>